import axios from 'axios';

import jwt_decode from 'jwt-decode';
import Vue from 'vue';
import Vuex from 'vuex';
import { Authenticate, endpoints } from '../endpoints/endpoints';

import { handleError } from '@/utils/helperFunctions';
import { USER_ROLES } from '@/utils/roles';
import _ from 'lodash';
import {
  AccountSummary,
  PortalNotification,
  Product,
  State,
} from '../definitions/definitions';
import client from '../endpoints';
import router from '../router/index';
import { alerts } from './alerts';
import { baskets } from './baskets';
import { emails } from './emails';
import { onlineDocs } from './online-docs';
import openAccount from './openAccount';
import { products } from './products';
import { utils } from './utils';
import { watchlist } from './watchlist';

import { ILog } from '@/definitions/log';
import logger from '@/logger/logging-token';
import { dueDiligence } from './dueDiligence';
import { publishLogout, publishTokenOffer } from '@/utils/auth-tab-sync';
import { readSessionToken } from '@/utils/session-token';

Vue.use(Vuex);

let loggingOut = false;

const defaultState: State = {
  account: {
    token: readSessionToken(),
    username: null,
    standardFees: null,
    details: {
      name: null,
      bostonId: null,
      userId: null,
      email: null,
      phone: null,
      company: 'Capital Platforms',
      companyCode: 'CP',
      companyName: '',
      subIfa: '',
      consultantName: '',
      hasSubIfa: false,
      whiteLabelId: '',
      role: null,
      roleType: null,
      cphqUser: null,
      impersonator: null,
      impersonatorRoles: null,
      expiry: null,
      tradingRights: null,
      isUserAllowedToPurchase: false,
      isUserAllowedToSell: false,
      transactionFeeSigned: false,
      childs: null,
      networkMembers: null,
      networkAdminDirectory: null,
      activeAccount: {
        id: null,
        userId: null,
        moventumId: null,
        type: null,
        currency: '',
        currencySymbol: null,
        creationDate: null,
        cashIn: null,
        cashOut: null,
        cashPosition: null,
        chargesOut: null,
        holdingsIn: null,
        holdingsOut: null,
        securityAndCashPosition: null,
        securityPosition: null,
      },
      consultantSummary: null,
      consultantClientList: null,
      consultantClientListForWatchlist: null,
      adminClientList: null,
      investmentSummary: null,
      activeAccountId: null,
      moventumId: null,
      consultantList: null,
    },
    accountSummary: [
      {
        id: null,
        moventumId: null,
        type: null,
        cashIn: null,
        cashOut: null,
        cashPosition: null,
        chargesOut: null,
        creationDate: null,
        currency: null,
        currencySymbol: null,
        holdingsIn: null,
        holdingsOut: null,
        isDFM: false,
        securityAndCashPosition: null,
        securityPosition: null,
        AllowedToPurchase: null,
        AllowedToSell: null,
        AvailableBalance: null,
        MinCashBalance: null,
        MinCashBalancePercentage: null,
        AccountNumber: null,
        PendingOrderAmount: null,
        accountSummaryError: false,
      },
    ],
    CMA: null,
    statements: null,
    statementUID: null,
    holdings: [],
    nonMoventumHoldings: [],
    nonMoventumHoldingsTotal: null,
    pendingOrders: null,
    docusignOrders: null,
    holdingTransactions: null,
  },
  rssFeed: {
    rss: {
      channel: [
        {
          title: '',
          item: [],
        },
      ],
    },
  },
  commentaries: null,
  rssFailed: false,
  currencyRates: [],
  cashPosition: null,
  referenceCurrency: null,
  consultantDefaultCurrency: null,
  activeTab: 'mutual-funds',
  searchResults: '',
  yieldCalculator: false,
  yieldCalculatorAdvanced: false,
  advancedCalculatorDetails: {
    investmentAmount: 0,
    term: 0,
    interestRate: 0,
  },
  loadingSearchResults: false,
  fundDetailsModal: {
    open: false,
    details: null,
  },
  fundSummaryModal: {
    open: false,
    details: null,
  },
  modal: {
    message: '',
  },
  buySell: false,
  emailFailed: false,
  accountStatementModal: false,
  accountStatementSettingsModal: false,
  userTransactions: {
    id: null,
    name: null,
    username: null,
    details: null,
    open: false,
  },
  userDocuments: null,
  dealingInstructions: null,
  notificationsModal: false,
  errors: '',
  isLoading: false,
  subdomain: null,
  appWidth: 0,
  appHeight: 0,
  productDetails: null,
  accountSummaryOutOfDate: null,
  acceptedDocuments: [],
  missingDocuments: null,
  missingDocument: null,
  missingDocumentModal: false,
  isMissingDocumentSubmitting: false,
  isMissingDocumentSubmitted: false,
  kyc: {
    fetching: false,
  },
  KYCModal: false,
  DocuSignModal: false,
  missingDocumentActiveTab: 'incomplete',
  missingDocumentFileUpload: false,
  updateProfileReminderModal: false,
  updateProfileReminderError: false,
  profileDetails: null,
  licenses: [],
  missingDocumentError: false,
  acceptedDocumentError: false,
  uploadDocumentError: false,
  submitDocumentError: false,
  openEFModal: false,
  openAccessModal: false,
  isCartLoading: false,
  portalNotificationModal: false,
  portalNotifications: [],
  portalNotificationIndex: 0,
};

// The Vuex store is used to manage and manipulate the state of data in a complex Vue application
export default new Vuex.Store({
  // The state is essentially just an object.
  state: { ...defaultState },
  getters: {
    isClientReadonly(state): boolean {
      return (
        state.account.details.role?.includes(USER_ROLES.CLIENT_READONLY) ||
        false
      );
    },
  },
  // Mutations can alter the state and are usually triggered by actions in the Store.
  mutations: {
    // sets the auth token to the store
    setToken(state, data) {
      state.account.token = data;
    },
    setUsername(state, data) {
      state.account.username = data;
    },
    async resetState(state) {
      Object.assign(state, { ...defaultState });
      await router.push('/login');
      router.go(0);
    },
    // Set active account id number
    setActiveAccountId(state, data) {
      state.account.details.activeAccountId = data;
    },
    setActiveAccount(state, data) {
      state.account.details.activeAccount = data;
    },
    // Set Account Username
    setName(state, data) {
      state.account.details.name = data.split(':').slice(0, 1)[0];
    },
    // Set Account Email
    setEmail(state, data) {
      state.account.details.email = data;
    },
    // Set User Role
    setUserRole(state, data) {
      state.account.details.role = data;
    },
    // Set User Role Type
    setUserRoleType(state, data) {
      state.account.details.roleType = data;
    },

    // Set User Role Type
    setCphqUser(state, data) {
      state.account.details.cphqUser = data;
    },
    // Set User Role Type
    setImpersonator(state, data) {
      state.account.details.impersonator = data;
    },
    setImpersonatorRoles(state, data) {
      state.account.details.impersonatorRoles = data;
    },
    setChilds(state, data) {
      state.account.details.childs = data;
    },
    setNetworkMembers(state, data) {
      state.account.details.networkMembers = data;
    },
    setNetworkAdminDirectory(state, data) {
      state.account.details.networkAdminDirectory = data;
    },

    // Set User Trading Rights
    setTradingRights(state, data) {
      state.account.details.tradingRights = data;
    },
    // Set User Trading Rights
    setBuySell(state, data) {
      state.account.details.isUserAllowedToPurchase = data.buy;
      state.account.details.isUserAllowedToSell = data.sell;
    },
    // Set User Trading Rights
    setTransactionFeeSigned(state, data) {
      state.account.details.transactionFeeSigned = data;
    },
    // Set Phone Number
    setPhone(state, data) {
      state.account.details.phone = data;
    },
    // Set expiry Number
    setExpiry(state, data) {
      state.account.details.expiry = data;
    },
    // Set Company Name
    setCompanyName(state, data) {
      const code = data.match(/[^:]*/i)[0];
      state.account.details.company = data;
      state.account.details.companyCode = code;
      state.account.details.companyName = data.split(':')[1];
    },
    // Set Boston  ID
    setBostonId(state, data) {
      state.account.details.bostonId = data;
    },
    // Set User ID (UserInfoId)
    setUserId(state, data) {
      state.account.details.userId = data;
    },
    // Set White Label ID
    setWhiteLabelId(state, data) {
      state.account.details.whiteLabelId = data;
    },
    // Set White Label
    setSubdomain(state, data) {
      state.subdomain = data;
    },

    // Set Consultant Details - summary of all holdings
    setConsultantSummary(state, data) {
      state.account.details.consultantSummary = data;
    },

    // Set Consultant Details - summary of all holdings
    setConsultantClientList(state, data) {
      state.account.details.consultantClientList = data;
    },
    // Set Consultant Watchlist Details - summary of all holdings
    setConsultantClientListForWatchlist(state, data) {
      state.account.details.consultantClientListForWatchlist = data;
    },
    // Set Consultant Details - summary of all holdings
    setAdminClientList(state, data) {
      state.account.details.adminClientList = data;
    },
    // Set Investment Summary - summary of all holdings broken down with clients
    setInvestmentSummary(state, data) {
      state.account.details.investmentSummary = data;
    },
    // Set Consultant List Details
    setConsultantList(state, data) {
      state.account.details.consultantList = data;
    },
    // Set Account Summmary Error
    setAccountSummary(state, data) {
      state.account.accountSummary = data;
    },
    setAccountSummaryError(state, data) {
      state.account.accountSummary[0].accountSummaryError = true;
    },
    // Set Cash Management Account Summmary
    setCMA(state, data) {
      state.account.CMA = data;
    },
    setHoldings(state, data) {
      state.account.holdings = data;
    },
    setNonMoventumHoldings(state, data) {
      state.account.nonMoventumHoldings = data;
    },
    setNonMoventumHoldingsTotal(state, data) {
      state.account.nonMoventumHoldingsTotal = data;
    },
    setPendingOrders(state, data) {
      state.account.pendingOrders = data;
    },
    setDocusignOrders(state, data) {
      state.account.docusignOrders = data;
    },
    setHoldingTransactions(state, data) {
      state.account.holdingTransactions = data;
    },
    setCurrencyRates(state, data) {
      state.currencyRates = data;
    },
    setCashPosition(state, data) {
      state.cashPosition = data;
    },
    setReferenceCurrency(state, data) {
      state.referenceCurrency = data;
    },
    setConsultantDefaultCurrency(state, data) {
      state.consultantDefaultCurrency = data;
    },

    setActiveTab(state, data) {
      state.activeTab = data;
    },
    updateSearchResults(state, data) {
      state.searchResults = data;
    },
    setRssFeed(state, data) {
      state.rssFeed = data;
    },
    setModalContent(state, data) {
      state.modal.message = data;
    },
    rssFailed(state, data) {
      state.rssFailed = data;
    },
    emailFailed(state, data) {
      state.emailFailed = data;
    },
    addError(state, data: string) {
      state.errors = data;
    },
    toggleBuySell(state, data: boolean) {
      state.buySell = data;
    },
    changeWhitelabelStyle(state, data) {
      state.account.details.companyCode = data;
    },
    updateAppWidth(state, data) {
      state.appWidth = data;
    },
    updateAppHeight(state, data) {
      state.appHeight = data;
    },
    toggleAdvancedYieldCalculator(state, data) {
      state.yieldCalculatorAdvanced = data;
    },
    updateAdvancedCalculatorDetails(state, data) {
      state.advancedCalculatorDetails = data;
    },
    toggleFundDetailsModal(state, data) {
      state.fundDetailsModal.open = data;
      if (data === false) {
        state.fundDetailsModal.details = null;
      }
    },
    setFundDetails(state, data) {
      state.fundDetailsModal.details = data;
    },
    toggleFundSummaryModal(state, data) {
      state.fundSummaryModal.open = data;
    },
    setFundSummary(state, data) {
      state.fundSummaryModal.details = data;
    },
    toggleAccountStatementModal(state, data) {
      state.accountStatementModal = data;
    },
    toggleAccountStatementSettingsModal(state, data) {
      state.accountStatementSettingsModal = data;
    },
    toggleSearchLoading(state, data) {
      state.loadingSearchResults = data;
    },
    setAccountStatements(state, data) {
      state.account.statements = data;
      state.accountStatementModal = Boolean(data);
      if (data) {
        state.accountStatementSettingsModal = false;
      }
    },
    setAccountStatementUID(state, data) {
      state.account.statementUID = data;
    },
    toggleUserTransactions(state, data) {
      state.userTransactions.id = data.id;
      state.userTransactions.name = data.name;
      state.userTransactions.username = data.username;
      state.userTransactions.accountNumber = data.accountNumber;
      state.userTransactions.open = data.open;
      state.userTransactions.details = null;
    },
    setUserTransactions(state, data) {
      state.userTransactions.details = data;
    },
    setUserDocuments(state, data) {
      state.userDocuments = data;
    },
    setStandardFees(state, data) {
      state.account.standardFees = data;
    },
    updateDealingInstructions(state, data) {
      state.dealingInstructions = data;
    },
    toggleNotificationsModal(state, data) {
      state.notificationsModal = data;
    },
    buyFundDetails(state, data) {
      state.productDetails = data;
    },
    setAccountSummaryOutOfDate(state, data) {
      state.accountSummaryOutOfDate = data;
    },
    setMissingDocuments(state, data) {
      state.missingDocuments = data;
    },
    setAcceptedMissingDocuments(state, data) {
      state.acceptedDocuments = data;
    },
    setMissingDocument(state, data) {
      state.missingDocument = data;
    },
    setMissingDocumentIsSubmitting(state, data) {
      state.isMissingDocumentSubmitting = data;
    },
    setMissingDocumentIsSubmitted(state, data) {
      state.isMissingDocumentSubmitted = data;
    },
    setSubmitDocumentError(state, data) {
      state.submitDocumentError = data;
    },
    toggleAcceptedDocumentError(state, data) {
      state.acceptedDocumentError = data;
    },
    toggleMissingDocumentError(state, data) {
      state.missingDocumentError = data;
    },
    toggleUploadDocumentError(state, data) {
      state.uploadDocumentError = data;
    },
    toggleMissingDocumentModal(state: any, data: string) {
      state.missingDocumentModal = data;
    },
    toggleKYCModal(state: any, data: string) {
      state.KYCModal = data;
    },
    toggleDocuSignModal(state: any, data: string) {
      state.DocuSignModal = data;
    },
    setMissingDocumentActiveTab(state: any, data: string) {
      state.missingDocumentActiveTab = data;
    },
    updateProfileReminderError(state: any, data: boolean) {
      state.updateProfileReminderError = data;
    },
    setFetching(state: any, data: boolean) {
      state.fetching = data;
    },
    setMissingDocumentFileUpload(state: any, data: boolean) {
      state.missingDocumentFileUpload = data;
    },
    toggleUpdateProfileReminderModal(state, data: boolean) {
      state.updateProfileReminderModal = data;
    },
    setPortalNotifications(state, data: PortalNotification[]) {
      state.portalNotifications = data;
    },
    setPortalNotificationIndex(state, data: number) {
      state.portalNotificationIndex = data;
    },
    togglePortalNotificationModal(state, data: boolean) {
      state.portalNotificationModal = data;
    },
    setProfileDetails(state, data) {
      state.profileDetails = data;
    },
    setSubIfa(state, data) {
      state.account.details.subIfa = data;
    },
    setHasSubIfa(state, data) {
      state.account.details.hasSubIfa = data;
    },
    setConsultantName(state, data) {
      state.account.details.consultantName = data;
    },
    setLicenses(state, data) {
      state.licenses = data;
    },
    openEFModal(state, data: boolean) {
      state.openEFModal = data;
    },
    openAccessModal(state: any, data: boolean) {
      state.openAccessModal = data;
    },
    setIsCartLoading(state, data: boolean) {
      state.isCartLoading = data;
    },
  },
  // Actions usually contain some form of crud operation
  // or used as a way of changing data before activating a mutation to directly manipulate the state
  actions: {
    // Makes request to the API for Auth Token using a with the provided username and password.
    async login(
      { commit },
      data: Authenticate & { hostCode?: string },
    ) {
      this.state.isLoading = true;
      try {
        const response = await axios.post(endpoints.authenticate, {
          UserName: data.UserName,
          Password: data.Password,
        });
        const token = response.data.Data.Value as string;

        let decoded: any = null;
        try {
          decoded = jwt_decode(token);
        } catch {
          /* ignore */
        }

        const companyCode = String(decoded?.company || '')
          .split(':')[0]
          .toLowerCase()
          .substring(0, 3);
        const hostCode = (data.hostCode || '').toLowerCase().substring(0, 3);
        const hostMismatch =
          !!hostCode && !!companyCode && companyCode !== hostCode;

        // Alias login, or credentials for a different WL than this portal host:
        // do not hydrate session / fire portal APIs until the UI handles redirect/notice.
        if (decoded?.relocatedFrom || hostMismatch) {
          this.state.isLoading = false;
          return {
            token,
            relocated: true as const,
            hostMismatch: hostMismatch as boolean,
          };
        }

        await this.dispatch('setToken', token);
        await this.dispatch('setUserDetails', token);
        this.dispatch('setUsername', data.UserName);
        document.title = this.state.account.details.company;
        this.state.isLoading = false;
        return {
          token,
          relocated: false as const,
          hostMismatch: false as boolean,
        };
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! Looks like something went wrong while trying to login, please try again',
        );
        this.dispatch('addError', msg);
        this.state.isLoading = false;
        return {
          token: null,
          relocated: false as const,
          hostMismatch: false as boolean,
        };
      }
    },
    async completeLoginWithToken(
      { commit },
      payload: { token: string; typedUsername?: string },
    ) {
      const { token, typedUsername } = payload;
      this.state.isLoading = true;
      try {
        await this.dispatch('setToken', token);
        await this.dispatch('setUserDetails', token);
        if (typedUsername) {
          this.dispatch('setUsername', typedUsername);
        }
        document.title = this.state.account.details.company;
      } finally {
        this.state.isLoading = false;
      }
    },
    logout(
      { commit },
      payload: { broadcast?: boolean } = {},
    ) {
      if (loggingOut) return;
      loggingOut = true;
      try {
        this.dispatch('setToken', '');
        this.dispatch('setUsername', '');
        localStorage.removeItem('role');
        if (payload?.broadcast !== false) {
          publishLogout();
        }
        this.commit('resetState');
      } finally {
        // resetState reloads the page; keep flag until then to absorb duplicate 401/LOGOUT
        window.setTimeout(() => {
          loggingOut = false;
        }, 0);
      }
    },

    async resetPassword(
      { commit, state },
      { type, data }: { type: 'submit' | 'reset'; data: any },
    ) {
      switch (type) {
        case 'reset':
          await axios.post(endpoints.updatePassword, data).catch((e) => {
            throw new Error(e.response.data.Message);
          });
          break;
        case 'submit':
          await axios
            .post(endpoints.resetPassword + '?userName=' + data.userName)
            .then((response) => {
              if (response.status !== 200) {
                throw new Error('Password reset faild');
              }
              // Added in check to display more accurate confirmation message depending on if it is for the current user or one of their client's passwords being reset.
              router.currentRoute.path === '/reset-password'
                ? this.dispatch('addError', response.data.Message)
                : this.dispatch(
                    'addError',
                    "An email has been sent to your client's email address with instructions on how to reset their password.",
                  );
            })
            .catch((err) => {
              const msg = handleError(
                err,
                'Sorry! Looks like something went wrong while trying to reset your password, please try again',
              );
              this.dispatch('addError', msg);
              return;
            });
          break;
      }
    },

    setUsername({ commit }, username) {
      localStorage.setItem('username', username);
      commit('setUsername', username);
    },
    // Sets auth token in sessionStorage and commits it to the store
    async setToken({ commit }, token) {
      client.setBearerToken(token);
      commit('setToken', token);

      if (token !== '' && token !== undefined && token !== null) {
        logger.loggingNewToken(token);
        publishTokenOffer(token);
      }
    },
    /** Apply a token from another open tab without rebroadcasting. */
    async applyPeerToken({ commit }, token: string) {
      if (!token) return;
      client.setBearerToken(token);
      commit('setToken', token);
      await this.dispatch('setUserDetails', token);
    },
    async setWhiteLabelId({ commit }, id) {
      commit('setWhiteLabelId', id);
    },
    // clears impersonated token and closes that window, logging the user out.
    async clearClientToken({ commit }) {
      window.sessionStorage.removeItem('clientToken');
      window.sessionStorage.removeItem('consultantName');
      window.close();
      if (this.state.account.details.cphqUser) {
        this.dispatch('logout');
      }
    },
    // Sets all the account information after getting token from login
    async setUserDetails({ commit }, token: string) {
      let decodedToken: any = null;
      try {
        decodedToken = jwt_decode(token);
      } catch (e) {
        console.error('Error decoding JWT:', e);
        return; // exit early if token is invalid
      }
      const safelyGet = (key: string, defaultValue: any = null) =>
        decodedToken?.[key] || defaultValue;
      const tradingRights = {
        isAllowedToPurchase: safelyGet('isUserAllowedToPurchase') === 'True',
        isAllowedToSell: safelyGet('isUserAllowedToSell') === 'True',
      };

      commit(
        'setTransactionFeeSigned',
        safelyGet('transactionFeeSigned') === 'True',
      );
      commit(
        'setWhiteLabelId',
        safelyGet('company', '').split(':').pop().trim(),
      );
      commit('setBostonId', safelyGet('nameid'));
      commit('setUserId', safelyGet('roleinfoId'));
      commit('setUserRole', safelyGet('role', '').split(','));
      commit('setTradingRights', tradingRights);
      commit('setBuySell', tradingRights);
      commit('setUserRoleType', safelyGet('tokentype'));
      commit('setCphqUser', safelyGet('cphqUser'));
      commit('setImpersonator', safelyGet('impersonator'));
      const impersonatorRolesRaw = safelyGet('impersonatorRoles', '');
      commit(
        'setImpersonatorRoles',
        impersonatorRolesRaw
          ? String(impersonatorRolesRaw)
              .split(',')
              .map((r: string) => r.trim())
              .filter(Boolean)
          : [],
      );
      commit('setChilds', safelyGet('childs', ''));
      commit('setNetworkMembers', safelyGet('networkMembers', ''));
      commit('setExpiry', safelyGet('exp'));
      commit('setCompanyName', safelyGet('company'));
      commit('setEmail', safelyGet('email'));
      commit('setPhone', safelyGet('mobilephone'));
      commit('setName', safelyGet('unique_name'));

      this.dispatch('setUsername', safelyGet('accountNo'));

      const role = safelyGet('role', '').split(',');
      const accountIds = safelyGet('accounts', '')
        .split(',')
        .reverse()
        .map(Number);

      if (accountIds.length) {
        await this.dispatch('getAccountSummary', { ids: accountIds });
      }

      if (role.includes('client')) {
        commit('setActiveAccountId', accountIds[0]);
        this.dispatch('getCashPosition');
        this.dispatch('getMissingDocuments', { status: 'incomplete' });
        this.dispatch('getProfileReminder');
      }

      if (!safelyGet('cphqUser')) {
        this.dispatch('fetchPortalNotifications');
      }

      if (
        role.some((r: string) =>
          [
            'consultant',
            'whitelabeladmin',
            'ownerofbusiness',
            'networkadmin',
          ].includes(r),
        )
      ) {
        commit('setActiveAccountId', accountIds[0]);
        await this.dispatch('getCurrencyRates');
        await this.dispatch('updateReferenceCurrency', 'EUR');
        await this.dispatch('setConsultantDefaultCurrency', 'EUR');
        await this.dispatch('getConsultantSummary', 'EUR');
        await this.dispatch('getConsultantClientList', 'EUR');
        await this.dispatch('getInvestmentSummary');
        await this.dispatch('getMissingDocuments', { status: 'incomplete' });
      }

      if (
        role.includes('whitelabeladmin') ||
        role.includes('ownerofbusiness') ||
        role.includes('networkadmin')
      ) {
        await this.dispatch('getAdminClientList');
      }

      const impersonatorRoles = this.state.account.details.impersonatorRoles || [];
      const isNetworkAdminImpersonation =
        safelyGet('tokentype') === 'impersonated' &&
        impersonatorRoles.includes('networkadmin');

      if (role.includes('client') && !isNetworkAdminImpersonation) {
        this.dispatch('getCart');
        this.dispatch('getHoldings');
        this.dispatch('getNonMoventumHoldings');
        this.dispatch('getNonMoventumHoldingsTotal');
      } else if (role.includes('client') && isNetworkAdminImpersonation) {
        commit('setTradingRights', {
          isAllowedToPurchase: false,
          isAllowedToSell: false,
        });
        commit('setBuySell', { buy: false, sell: false });
        this.dispatch('getHoldings');
        this.dispatch('getNonMoventumHoldings');
        this.dispatch('getNonMoventumHoldingsTotal');
      }

      this.dispatch('getNotesProviders');
      this.dispatch('getStructuredNotes');
      this.dispatch('getFixedIncome');
      this.dispatch('getFixedIncomeProviders');
      this.dispatch('getEquities');
      this.dispatch('getFundsSummary', 'Equities');
    },

    // Send CPHQ token for improved logging
    async CPHQAuth({ commit }, data) {
      const res = await axios
        .post(endpoints.CPHQAuth, data, {
          headers: {
            Authorization: 'bearer ' + data.token,
          },
        })
        .then((response) => {
          return;
        })
        .catch((err) => {
          console.log(err);
          return;
        });
    },
    // Consultant login as client
    async loginAs({ commit }, id) {
      this.state.isLoading = true;
      const data = {
        clientBostonId: parseInt(id, 10),
        role: this.state.account.details.role
          ? this.state.account.details.role[0]
          : '',
      };
      const bostonId = this.state.account.details.bostonId
        ? this.state.account.details.bostonId.toString()
        : '';
      const res = await axios
        .post(endpoints.consultantClientAccess, data, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          const newInstance = window.open('/');
          const cphqUser: string = this.state.account.details.cphqUser
            ? this.state.account.details.cphqUser
            : '';
          const name: string = this.state.account.details.name
            ? this.state.account.details.name
            : '';
          const bostonIdRes: string = this.state.account.details.bostonId
            ? this.state.account.details.bostonId.toString()
            : '';
          newInstance?.sessionStorage.setItem(
            'clientToken',
            response.data.Data.Value,
          );
          newInstance?.sessionStorage.setItem('consultantName', name);
          newInstance?.sessionStorage.setItem(
            'consultantBostonID',
            bostonIdRes,
          );
          newInstance?.sessionStorage.setItem('cphqUser', cphqUser);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to login as your client',
          );
          this.dispatch('addError', msg);
          this.state.isLoading = false;
        });
      this.state.isLoading = false;
    },

    async getConsultantSummary({ commit }, data) {
      const res = await axios
        .get(
          endpoints.consultantSummary +
            this.state.account.details.bostonId +
            '/' +
            data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setConsultantSummary', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          if (err.response.status === 401) {
            this.dispatch(
              'addError',
              'Sorry you have been logged out due to inactivity or timeout, please login again',
            );
            setTimeout(() => {
              this.dispatch('logout');
            }, 2500);
          } else {
            const msg = handleError(
              err,
              'Sorry! Looks like something went wrong while trying to access your account summary',
            );
            this.dispatch('addError', msg);
          }
        });
    },
    async submitMissingDocument({ commit }, data) {
      const formData = new FormData();
      formData.append('file', data.data.fileUpload);
      formData.append('userMessage', data.data.userMessage);
      formData.append('status', 'uploaded');
      this.commit('setSubmitDocumentError', false);

      const res = await axios
        .put(
          endpoints.missingDocuments +
            '/' +
            data.missingDocumentId +
            '?whiteLabelId=' +
            this.state.account.details.whiteLabelId,
          formData,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then((response) => {
          console.log(response);
          this.commit('setMissingDocumentIsSubmitted', false);
          // this.dispatch('getMissingDocuments', {status: 'incomplete', whiteLabelId: this.state.account.details.whiteLabelId});
          return;
        })
        .catch((err) => {
          this.commit('setSubmitDocumentError', true);
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to submit missing document',
          );
          this.dispatch('addError', msg);
          return err;
        });
    },
    async submitKYCDocument({ commit }, data) {
      const res = await axios
        .put(
          endpoints.missingDocuments +
            '/' +
            data.missingDocumentId +
            '/kyc?whiteLabelId=' +
            this.state.account.details.whiteLabelId,
          data.data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          this.dispatch('getMissingDocuments', {
            status: 'incomplete',
            whiteLabelId: this.state.account.details.whiteLabelId,
            isModalOpened: false,
          });
          return;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to submit KYC document',
          );
          this.dispatch('addError', msg);
          return err;
        });
    },
    async submitDocusignDocument({ commit }, data) {
      const res = await axios
        .post(
          endpoints.docusignDocument +
            '?whiteLabelId=' +
            this.state.account.details.whiteLabelId,
          data.data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          this.dispatch('getMissingDocuments', {
            status: 'incomplete',
            whiteLabelId: this.state.account.details.whiteLabelId,
            isModalOpened: false,
          });

          return;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to submit docusign document',
          );
          this.dispatch('addError', msg);
          return err;
        });
    },
    async getMissingDocuments({ commit }, data) {
      const status = data !== undefined ? data.status : 'incomplete';
      this.commit('setMissingDocumentIsSubmitted', true);
      const res = await axios
        .get(
          endpoints.missingDocuments +
            `?whitelabelID=${
              this.state.account.details.whiteLabelId ?? 9999
            }&status=` +
            status,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('toggleMissingDocumentError', false);
          commit('toggleAcceptedDocumentError', false);

          if (response.data.data.length > 0) {
            if (status === 'approved') {
              commit('setAcceptedMissingDocuments', response.data.data);
            } else if (status === 'uploaded') {
              commit('toggleUploadDocumentError', true);
            } else {
              const missingDocumentList = response.data.data;
              const groupMissingDocumentList = _.groupBy(
                missingDocumentList,
                'Client.AccountName',
              );
              const listOfMissingDocuments = Object.keys(
                groupMissingDocumentList,
              );
              const summaryMissingDocuments: any[] = [];
              listOfMissingDocuments.forEach((key) => {
                const newData = {
                  FirstName: '',
                  LastName: '',
                  AccountName: '',
                  Documents: [] as any,
                };
                newData.AccountName = key;

                const documentsInGroup = groupMissingDocumentList[key];
                documentsInGroup.forEach((item) => {
                  newData.Documents.push(item);
                  newData.FirstName = item.Client.FirstName;
                  newData.LastName = item.Client.LastName;
                });
                summaryMissingDocuments.push(newData);
              });

              commit('setMissingDocuments', {
                groups: summaryMissingDocuments,
                list: missingDocumentList,
              });
            }

            if (data && data.isModalOpened === false) {
              console.log('false');
              commit('toggleMissingDocumentModal', false);
            } else {
              console.log('true');
              commit('toggleMissingDocumentModal', true);
            }
          } else {
            if (status === 'approved') {
              commit('setAcceptedMissingDocuments', []);
            } else if (status === 'uploaded') {
              commit('toggleUploadDocumentError', false);
            } else {
              commit('setMissingDocuments', { groups: [], list: [] });
            }
            commit('toggleMissingDocumentModal', false);
          }

          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the missing documents values',
          );
          // TODO - handle properly as currently throwing error to user for 403 'forbidden'.
          // this.dispatch('addError', msg);

          if (status === 'approved') {
            console.log('approved list');
            commit('toggleAcceptedDocumentError', true);
          } else {
            console.log('missing list');
            commit('toggleMissingDocumentError', true);
          }
        })
        .finally(() => {
          // this.commit('setMissingDocumentIsSubmitted', false)
        });
    },
    async getConsultantClientList({ commit }, data) {
      const res = await axios
        .get(
          endpoints.consultantClientList +
            this.state.account.details.bostonId +
            '/' +
            data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setConsultantClientList', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get your client list',
          );
          this.dispatch('addError', msg);
          return err;
        });
    },
    async getConsultantClientListForWatchlist({ commit }, data) {
      const res = await axios
        .get(
          endpoints.consultantClientList +
            this.state.account.details.bostonId +
            '/' +
            data.currency,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setConsultantClientListForWatchlist', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get your client list',
          );
          this.dispatch('addError', msg);
          return err;
        });
    },
    async getAdminClientList({ commit }, data) {
      const roles = this.state.account.details.role || [];
      const isNetworkAdmin = roles.includes('networkadmin');
      const headers = {
        Authorization: 'bearer ' + this.state.account.token,
      };

      const fetchForWhiteLabel = async (whiteLabelId: string | number) => {
        const response = await axios.get(
          endpoints.adminClientList + '?whitelabelId=' + whiteLabelId,
          { headers },
        );
        return response.data.Data || [];
      };

      try {
        if (!isNetworkAdmin) {
          const list = await fetchForWhiteLabel(
            this.state.account.details.whiteLabelId,
          );
          commit('setAdminClientList', list);
          commit('setNetworkAdminDirectory', null);
          return list;
        }

        // Single API: White Label + Org members (Sub-IFA) with consultants/clients
        const networkCompanyId = this.state.account.details.whiteLabelId;
        const response = await axios.get(
          endpoints.networkAdminDirectory +
            '?networkCompanyId=' +
            networkCompanyId,
          { headers },
        );
        const rawDirectory = response.data.Data || [];
        const directory = (rawDirectory || []).map((ifa: any) => ({
          companyInfoId: ifa.CompanyInfoId ?? ifa.companyInfoId,
          companyCode: ifa.CompanyCode ?? ifa.companyCode ?? '',
          companyName:
            ifa.CompanyName ?? ifa.companyName ?? `IFA ${ifa.CompanyInfoId}`,
          memberType: ifa.MemberType ?? ifa.memberType ?? '',
          subIfaId: ifa.SubIfaId ?? ifa.subIfaId ?? null,
          consultants: ifa.Consultants ?? ifa.consultants ?? [],
        }));

        const flat = directory.flatMap((ifa: any) =>
          (ifa.consultants || []).map((c: any) => ({
            ...c,
            CompanyInfoId: ifa.companyInfoId,
            CompanyName: ifa.companyName,
          })),
        );
        commit('setAdminClientList', flat);
        commit('setNetworkAdminDirectory', directory);
        return directory;
      } catch (err) {
        const msg = handleError(
          err,
          'Sorry! Looks like something went wrong while trying to get your client list',
        );
        this.dispatch('addError', msg);
        return err;
      }
    },

    async getConsultantList({ commit }, data) {
      const res = await axios
        .get(
          endpoints.consultantList +
            '?whitelabelID=' +
            this.state.account.details.whiteLabelId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setConsultantList', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to access the consultant list',
          );
          this.dispatch('addError', msg);
        });
    },
    async getInvestmentSummary({ commit }, data) {
      const res = await axios
        .get(
          endpoints.investmentSummary +
            '?whitelabelID=' +
            this.state.account.details.whiteLabelId +
            '&consultantId=' +
            this.state.account.details.bostonId +
            '&getLatest=true',
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setInvestmentSummary', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to access the investment summary',
          );
          this.dispatch('addError', msg);
        });
    },

    async getAccountSummary({ commit }, data: any) {
      let accData;
      const formattedAccounts: any[] = [];
      const res = await axios
        .get(endpoints.accountSummary + data.ids, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          if (err.response.status === 401) {
            this.dispatch(
              'addError',
              'Sorry you have been logged out due to inactivity or timeout, please login again',
            );
            setTimeout(() => {
              this.dispatch('logout');
            }, 2500);
          } else {
            const msg = handleError(
              err,
              'Sorry! Looks like something went wrong while trying to get your account summary',
            );
            this.dispatch('addError', msg);
            commit('setAccountSummaryError');
          }
        });
      if (!res?.Data) return;
      accData = res.Data;
      await accData.forEach((acc: any, i: number) => {
        const formattedAccData: AccountSummary = {
          id: acc.Id,
          cashIn: acc.CashIn,
          cashOut: acc.CashOut,
          cashPosition: acc.CashPosition,
          chargesOut: acc.ChargesOut,
          creationDate: acc.CreationDate,
          currency: acc.Currency,
          currencySymbol: acc.CurrencySymbol,
          isDFM: acc.IsDFM ? acc.IsDFM : false,
          holdingsIn: acc.HoldingsIn,
          holdingsOut: acc.HoldingsOut,
          moventumId: acc.Number,
          securityAndCashPosition: acc.SecurityAndCashPosition,
          securityPosition: acc.SecurityPosition,
          type: acc.Type,
          AllowedToPurchase: acc.AllowedToPurchase,
          AllowedToSell: acc.AllowedToSell,
          AvailableBalance: acc.AvailableBalance,
          MinCashBalance: acc.MinCashBalance,
          MinCashBalancePercentage: acc.MinCashBalancePercentage,
          AccountNumber: acc.Number,
          PendingOrderAmount: acc.PendingOrderAmount,
          accountSummaryError: false,
        };
        formattedAccounts.push(formattedAccData);
      });
      if (res.Message !== null) {
        commit('setAccountSummaryOutOfDate', res.Message);
      }
      if (data.activeAccount) {
        commit('setActiveAccountId', data.activeAccount);
      } else {
        commit('setActiveAccountId', data.ids[0]);
      }
      commit('setAccountSummary', formattedAccounts);
      this.dispatch('getCurrencyRates');
    },

    async getCMA({ commit }, data: any) {
      const res = await axios
        .get(
          endpoints.CMA +
            '?accountId=' +
            this.state.account.details.activeAccountId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setCMA', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          if (err.response.status === 401) {
            this.dispatch(
              'addError',
              'Sorry you have been logged out due to inactivity or timeout, please login again',
            );
            setTimeout(() => {
              this.dispatch('logout');
            }, 2500);
          } else {
            const msg = handleError(
              err,
              'Sorry! Looks like something went wrong while trying to get your Cash Management Account details',
            );
            this.dispatch('addError', msg);
          }
        });
    },

    setActiveAccountId({ commit }, id) {
      commit('setActiveAccountId', id);
    },

    setActiveAccount({ commit }, acc) {
      commit('setActiveAccount', acc);
    },

    async getHoldings({ commit }) {
      const res = await axios
        .get(endpoints.holdings + this.state.account.details.activeAccountId, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          commit('setHoldings', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          this.dispatch('addError', msg);
          commit('setHoldings', []);
        });
    },
    async getNonMoventumHoldings({ commit }) {
      const res = await axios
        .get(
          endpoints.nonMoventumHoldings +
            '?userinfoID=' +
            this.state.account.details.userId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setNonMoventumHoldings', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          console.warn(msg);
          commit('setNonMoventumHoldings', []);
        });
    },
    async getNonMoventumHoldingsTotal({ commit }) {
      const res = await axios
        .get(
          endpoints.nonMoventumHoldingsTotal +
            '?userinfoID=' +
            this.state.account.details.userId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setNonMoventumHoldingsTotal', response.data.Data.totalValue);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          console.warn(msg);
          commit('setNonMoventumHoldingsTotal');
        });
    },
    async sellNonMoventumHolding({ commit }, data) {
      const res = await axios
        .post(endpoints.sellNonMoventumHoldings, data, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          this.commit('updateDealingInstructions', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! looks like something went wrong while trying to generate Sell Order Document(s)',
          );
          this.dispatch('addError', msg);
        });
      //
    },
    async getPendingOrders({ commit }) {
      const res = await axios
        .get(
          endpoints.pendingOrders +
            '?userinfoID=' +
            this.state.account.details.userId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setPendingOrders', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          console.warn(msg);
          commit('setPendingOrders', null);
        });
    },
    async getDocusignOrders({ commit }) {
      const res = await axios
        .get(
          endpoints.docusignOrders +
            '?userinfoID=' +
            this.state.account.details.userId +
            '&whitelabelID=' +
            this.state.account.details.whiteLabelId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setDocusignOrders', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to getting pending docusign orders',
          );
          console.warn(msg);
          commit('setDocusignOrders', null);
        });
    },

    async getCurrencyRates({ commit }) {
      const res = await axios
        .get(
          endpoints.currencyRates +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setCurrencyRates', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading currency rates',
          );
          this.dispatch('addError', msg);
        });

      // add here set ref currency
    },
    async getCashPosition({ commit }) {
      const res = await axios
        .get(
          endpoints.cashPosition +
            '?accountId=' +
            parseInt(this.state.account.details.activeAccountId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setCashPosition', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading your available cash position',
          );
          this.dispatch('addError', msg);
        });
    },

    updateReferenceCurrency({ commit }, data) {
      const updatedRefObj = this.state.currencyRates.filter((x: any) => {
        return x.Currency === data;
      });
      commit('setReferenceCurrency', updatedRefObj[0]);
    },
    setConsultantDefaultCurrency({ commit }, data) {
      const updatedRefObj = this.state.currencyRates.filter((x: any) => {
        return x.Currency === data;
      });
      commit('setConsultantDefaultCurrency', updatedRefObj[0]);
    },

    async getHoldingTransactions({ commit }, ISIN) {
      const res = await axios
        .get(
          endpoints.holdingTransactions +
            this.state.account.details.activeAccountId +
            '/' +
            ISIN,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while fetching the holding transactions',
          );
          this.dispatch('addError', msg);
        });
      commit('setHoldingTransactions', res.Data);
    },

    addError({ commit }, msg) {
      commit('addError', msg);
    },

    // Gets Token from local storage and commits it to the store.
    async getToken({ commit }) {
      const token = client.getBearerToken();
      if (token && token.length > 0) {
        commit('setToken', token);
      } else {
        commit('setToken', '');
      }
    },
    // Gets Username from local storage and commits it to the store.
    async getUsername({ commit }) {
      const username = localStorage.getItem('username');
      if (username && username.length > 0) {
        commit('setUsername', username);
      } else {
        commit('setUsername', '');
      }
    },

    setActiveTab({ commit }, data) {
      commit('setActiveTab', data);
    },

    scrollToActiveSection({ commit }, data) {
      const el = data;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    },

    openModal({ commit }, data) {
      commit('setModalContent', data);
    },

    toggleAdvancedYieldCalculator({ commit }, data) {
      commit('toggleAdvancedYieldCalculator', data);
    },
    updateAdvancedCalculatorDetails({ commit }, data) {
      commit('updateAdvancedCalculatorDetails', data);
    },
    async getFundDetails({ commit }, product: Product) {
      commit('setFundDetails', null);
      commit('toggleFundDetailsModal', true);
      this.commit('setFetching', true);
      commit('buyFundDetails', product);
      const res = await axios
        .get(endpoints.fundFactSheet + '?ISIN=' + product.ISIN, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          commit('setFundDetails', response.data.Data);
          commit('toggleFundDetailsModal', true);
          this.commit('setFetching', false);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading the fund details',
          );
          this.dispatch('addError', msg);
          this.commit('setFetching', false);
        });
    },
    async getFundDetailsURL({ commit }, ISIN: number) {
      const res = await axios
        .get(
          endpoints.fundFactSheetURL +
            '?ISIN=' +
            ISIN +
            '&whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          const a = document.createElement('a');
          a.target = '_blank';
          a.href = response.data.Data;
          a.download = response.data.Data.split('/').pop();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while downloading the fund details',
          );
          this.dispatch('addError', msg);
        });
    },

    async getFundSummary({ commit }, fund: any) {
      this.commit('setFetching', true);

      let url =
        endpoints.fundSummary +
        '?whitelabelID=' +
        parseInt(this.state.account.details.whiteLabelId, 10) +
        '&ISIN=' +
        fund.ISIN;
      if (
        this.state.account.details.role &&
        this.state.account.details.role.includes('client')
      ) {
        url += '&userinfoID=' + this.state.account.details.userId;
      }
      const res = await axios
        .get(url, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          response.data.Data.FundName = fund.FundName;
          commit('setFundSummary', response.data.Data);
          commit('toggleFundSummaryModal', true);
          this.commit('setFetching', false);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading the fund summary',
          );
          this.dispatch('addError', msg);
          this.commit('setFetching', false);
        });
    },

    toggleFundSummaryModal({ commit }, data) {
      commit('toggleFundSummaryModal', data);
    },
    toggleFundDetailsModal({ commit }, data) {
      commit('toggleFundDetailsModal', data);
    },

    toggleAccountStatementModal({ commit }, data) {
      commit('toggleAccountStatementModal', data);
    },

    toggleAccountStatementSettingsModal({ commit }, data) {
      commit('toggleAccountStatementSettingsModal', data);
    },

    async getRssFeeds({ commit }) {
      commit('rssFailed', false);
      const rssFeed = await axios
        .get(
          endpoints.rssFeed +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setRssFeed', response.data.Data);
          return;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while fetching rss feed',
          );
          console.warn(msg);
          commit('rssFailed', true);
          return;
        });
    },
    async getAccountStatement({ commit }, data) {
      const getLatest: boolean = data.latest;

      // get current date
      const date: Date = data.date ? data.date : new Date();
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
      const yyyy = date.getFullYear();
      const formattedDate = dd + '%2F' + mm + '%2F' + yyyy;
      const res = await axios
        .get(
          endpoints.accountStatement +
            '?platformID=' +
            parseInt(data.uid, 10) +
            '&statementDate=' +
            formattedDate +
            '&whitelabelID=' +
            parseInt(data.whitelabel, 10) +
            '&getLatest=' +
            getLatest,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          const statement = response.data && response.data.Data;
          if (
            !statement ||
            !Array.isArray(statement.StatementAccountInformation?.DSAccounts) ||
            !Array.isArray(statement.StatementCashBalanceInformation?.DSCash) ||
            !Array.isArray(statement.StatementHoldingInformation?.DSFunds) ||
            !Array.isArray(statement.StatementClientInformation?.DSClient) ||
            !statement.StatementClientInformation.DSClient.length ||
            !Array.isArray(statement.StatementRegionsInformation?.DSFundRegions) ||
            !Array.isArray(statement.AssetAllocationPercentage) ||
            !Array.isArray(statement.HoldingsCurrencyPercentage)
          ) {
            throw new Error('Account statement is unavailable');
          }
          commit('setAccountStatements', statement);
          commit('setAccountStatementUID', data.uid);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading your Account Statement',
          );
          this.dispatch('addError', msg);
          commit('setAccountStatements', null);
        });
    },
    async downloadAccountStatement({ commit }, data) {
      const getLatest: boolean = false;
      let formattedDate;
      if (data.date) {
        formattedDate = data.date.replaceAll('/', '%2F');
      } else {
        // get current date
        const date: Date = data.date ? data.date : new Date();
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
        const yyyy = date.getFullYear();
        formattedDate = dd + '%2F' + mm + '%2F' + yyyy;
      }
      let queryString: string =
        endpoints.accountStatementDownload +
        '?platformID=' +
        this.state.account.statementUID +
        '&statementDate=' +
        formattedDate +
        '&whitelabelID=' +
        parseInt(this.state.account.details.whiteLabelId, 10) +
        '&getLatest=' +
        getLatest;
      data.isEmail
        ? (queryString += '&isEmail=' + data.isEmail)
        : (queryString += '&isEmail=false');
      data.isClient
        ? (queryString += '&isClient=' + data.isClient)
        : (queryString += '&isClient=false');
      if (data.reportType) {
        queryString += '&reportType=' + data.reportType;
      }
      const res = await axios
        .get(queryString, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          if (data.isEmail) {
            return response.data;
          } else {
            const a = document.createElement('a');
            a.target = '_blank';
            a.href = response.data.Data;
            a.download = response.data.Data.split('/').pop();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return response.data;
          }
        })
        .catch((err) => {
          commit('emailFailed', true);
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while downloading your Account Statement',
          );
          return err;
        });
    },
    async openUserTransactions({ commit }, data) {
      commit('toggleUserTransactions', data);
    },
    async getUserTransactions({ commit }, data) {
      // get current date
      function formatDate(dateToFormat: Date) {
        const date: Date = dateToFormat;
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
        const yyyy = date.getFullYear();
        const formattedDate = dd + '%2F' + mm + '%2F' + yyyy;
        return formattedDate;
      }
      const startDate = formatDate(data.startDate);
      const endDate = formatDate(data.endDate);

      const res = await axios
        .get(
          endpoints.transactionHistory +
            '?platformID=' +
            parseInt(data.id, 10) +
            '&filterCashTransactionType="all"' +
            '&dateFrom=' +
            startDate +
            '&dateTo=' +
            endDate +
            '&getAllTransactions=' +
            data.getAllTransactions,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setUserTransactions', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading your Account Statement',
          );
          this.dispatch('addError', msg);
          commit('setUserTransactions', null);
        });
    },

    // get User Documents (for download on my account page)
    async getUserDocuments({ commit }) {
      const res = await axios
        .get(
          endpoints.userDocuments +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setUserDocuments', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while getting available documents',
          );
          this.dispatch('addError', msg);
        });
    },
    // get standard fees
    async getStandardFees({ commit }, feeId: number) {
      const res = await axios
        .get(
          endpoints.standardfees +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10) +
            '&feeSchedulesID=' +
            feeId,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('setStandardFees', response.data.Data);
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading fees',
          );
          this.dispatch('addError', msg);
        });
    },
    async createLog({ commit }, data) {
      const res = await axios
        .post(endpoints.createLog, data, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
            'Content-Type': 'application/json',
          },
        })
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          console.warn(msg);
        });
    },
    async userActivityLog({ commit }, data: ILog) {
      await axios
        .post(endpoints.userActivityLog, data, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while trying to get the account holdings',
          );
          console.warn(msg);
        });
    },
    updateDealingInstructions({ commit }, data) {
      commit('updateDealingInstructions', data);
    },
    toggleNotificationsModal({ commit }, data) {
      commit('toggleNotificationsModal', data);
    },
    toggleMissingDocumentModal({ commit }, data) {
      commit('toggleMissingDocumentModal', data);
    },
    setMissingDocumentIsSubmitting({ commit }, data) {
      commit('setMissingDocumentIsSubmitting', data);
    },
    setMissingDocumentIsSubmitted({ commit }, data) {
      commit('setMissingDocumentIsSubmitted', data);
    },
    toggleMissingDocumentError({ commit }, data) {
      commit('toggleMissingDocumentError', data);
    },
    toggleUploadDocumentError({ commit }, data) {
      commit('toggleUploadDocumentError', data);
    },
    toggleAcceptedDocumentError({ commit }, data) {
      commit('toggleAcceptedDocumentError', data);
    },
    toggleKYCModal({ commit }, data) {
      commit('toggleKYCModal', data);
    },
    toggleDocuSignModal({ commit }, data) {
      commit('toggleDocuSignModal', data);
    },
    setMissingDocumentActiveTab({ commit }, data) {
      commit('setMissingDocumentActiveTab', data);
    },
    setMissingDocumentFileUpload({ commit }, data) {
      commit('setMissingDocumentFileUpload', data);
    },
    toggleUpdateProfileReminderModal({ commit }, data) {
      commit('toggleUpdateProfileReminderModal', data);
    },
    async fetchPortalNotifications({ commit, state }) {
      if (state.account.details.cphqUser) {
        return;
      }

      try {
        const response = await axios.get(endpoints.notificationPopupsActive, {
          headers: {
            Authorization: 'bearer ' + state.account.token,
          },
        });
        const popups: PortalNotification[] =
          response.data?.Data?.Popups || [];
        if (popups.length) {
          commit('setPortalNotifications', popups);
          commit('setPortalNotificationIndex', 0);
          commit('togglePortalNotificationModal', true);
        }
      } catch (err) {
        // Non-blocking: login should continue if notifications fail.
      }
    },
    async dismissPortalNotification({ commit, state }) {
      const current =
        state.portalNotifications[state.portalNotificationIndex];
      if (current?.PopupId) {
        axios
          .post(
            `${endpoints.notificationPopupSeen}${current.PopupId}/seen`,
            null,
            {
              headers: {
                Authorization: 'bearer ' + state.account.token,
              },
            },
          )
          .catch(() => undefined);
      }

      const nextIndex = state.portalNotificationIndex + 1;
      if (nextIndex < state.portalNotifications.length) {
        commit('setPortalNotificationIndex', nextIndex);
        return;
      }

      commit('togglePortalNotificationModal', false);
      commit('setPortalNotifications', []);
      commit('setPortalNotificationIndex', 0);
    },

    changeWhitelabelStyle({ commit }, data) {
      commit('changeWhitelabelStyle', data);
    },
    setSubdomain({ commit }, data) {
      commit('setSubdomain', data);
    },
    openEFModal({ commit }, data) {
      commit('openEFModal', data);
    },
    // update Profile Reminder Modal
    async updateProfileReminder({ commit }, data) {
      const res = await axios
        .put(
          endpoints.userinfo +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          commit('updateProfileReminderError', true);
          return response.data;
        })
        .catch((err) => {
          commit('updateProfileReminderError', false);
          commit('toggleUpdateProfileReminderModal', false);
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while updating profile reminders',
          );
          this.dispatch('addError', msg);
          return;
        });
    },
    // get Profile Reminder Modal
    async getProfileReminder({ commit }) {
      const res = await axios
        .get(
          endpoints.userinfo +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          if (response.data != null) {
            commit('setSubIfa', response.data.data.subIfa);
            commit('setHasSubIfa', response.data.data.hasSubIfa);
            commit('setConsultantName', response.data.data.consultant);
            if (
              response.data.data.detailsUpdated === false ||
              response.data.data.acceptRoleStatement === false
            ) {
              commit('setProfileDetails', response.data.data);
              commit('toggleUpdateProfileReminderModal', true);
            }
          }
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while getting profile reminders',
          );
          this.dispatch('addError', msg);
        });
    },
    // get Licenses
    async getLicenses({ commit }) {
      const res = await axios
        .get(
          endpoints.licenses +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10),
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          if (response.data != null) {
            commit('setLicenses', response.data?.Data);
          }
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while getting licenses',
          );
          this.dispatch('addError', msg);
        });
    },
    openAccessModal({ commit }, data) {
      commit('openAccessModal', data);
    },
  },
  // Modules can be used to break up the store into small chunks - mostly used for larger applications
  modules: {
    alerts,
    baskets,
    openAccount,
    products,
    watchlist,
    utils,
    onlineDocs,
    emails,
    dueDiligence,
  },
});
